package com.tregz.tremenz.ui.component

import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tregz.design.motion.isReducedMotion
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import tregz.tremenz.shared.generated.resources.Res
import tregz.tremenz.shared.generated.resources.me_orange
import tregz.tremenz.shared.generated.resources.me_red_cue
import tregz.tremenz.shared.generated.resources.me_red_mix
import com.tregz.tremenz.ui.label.LabelKey
import com.tregz.tremenz.ui.label.labelText
import com.tregz.tremenz.ui.media.LocalPlayerOverlayTop

private val SLIDES: List<DrawableResource> = listOf(
    Res.drawable.me_red_cue,
    Res.drawable.me_red_mix,
    Res.drawable.me_orange
)

private const val CREDIT_LABEL = "Photos : Alexandre Deslauriers Photographe"
private const val SLIDE_DURATION_MS = 4000L
private const val TRANSITION_DURATION_MS = 800

@Composable
fun CarouselHero(
    modifier: Modifier = Modifier
) {
    val pagerState = rememberPagerState(initialPage = 0, pageCount = { SLIDES.size })

    // Auto-advancing slides are decorative motion, so they stop when the user asked for less of it,
    // and while an expanded tab covers the whole player.
    val reducedMotion = isReducedMotion()
    val isCovered = LocalPlayerOverlayTop.current?.let { it <= 0.dp } == true
    LaunchedEffect(pagerState, reducedMotion, isCovered) {
        while (!reducedMotion && !isCovered) {
            delay(SLIDE_DURATION_MS)
            if (!pagerState.isScrollInProgress) {
                val nextPage = (pagerState.currentPage + 1) % SLIDES.size
                pagerState.animateScrollToPage(
                    page = nextPage,
                    animationSpec = tween(durationMillis = TRANSITION_DURATION_MS)
                )
            }
        }
    }

    Box(modifier = modifier) {
        HorizontalPager(
            state = pagerState,
            modifier = Modifier.fillMaxSize()
        ) { page ->
            Image(
                painter = painterResource(SLIDES[page]),
                contentDescription = labelText(LabelKey.HERO_PHOTO),
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop
            )
        }

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.BottomCenter)
                .background(Color.Black.copy(alpha = 0.5f))
                .padding(horizontal = 12.dp, vertical = 6.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                repeat(SLIDES.size) { index ->
                    val isSelected = pagerState.currentPage == index
                    Box(
                        modifier = Modifier
                            .size(if (isSelected) 8.dp else 6.dp)
                            .clip(CircleShape)
                            .background(
                                if (isSelected) Color.White else Color.White.copy(alpha = 0.4f)
                            )
                    )
                }
            }

            Text(
                text = CREDIT_LABEL,
                color = Color.White.copy(alpha = 0.9f),
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium
            )
        }
    }
}
